import { useMemo } from 'react'
import useSharedCollection from '../firebase/useSharedCollection'
import useSharedCollectionWhere from '../firebase/useSharedCollectionWhere'
import { buildPersonaLocationIndex, withPersonaLocations } from '../helpers/locations/personaLocations'

/**
 * De deelnemers van deze organisatie, mét de locaties waarop ze werken.
 *
 * Vervangt `useSharedCollection('Personas')` op elke plek die op locatie
 * filtert. De locatie komt uit de ROOSTERREGELS — daar hoort hij sinds
 * 2026-07-06 — met een terugval op het oude veld op de deelnemer voor wie nog
 * geen roosterregel met een locatie heeft. Zie `helpers/locations/personaLocations.js`.
 *
 * Twee gedeelde listeners, allebei multicast: dezelfde `Personas` die er al was,
 * plus de roosterregels. Die laatste stonden op het dashboard toch al open voor
 * de agendakaart.
 *
 * AI-CONTEXT: See docs/features/huishouden-locaties.md (inbox #264)
 *
 * @returns {Array<object>} Deelnemers met een `LocationIDs`-lijst
 */
const usePersonaLocations = () => useWithPersonaLocations(useSharedCollection('Personas'))

/**
 * Hetzelfde, maar voor een lijst deelnemers die je zelf al hebt.
 *
 * Voor de twee plekken die deelnemers anders ophalen: `DashboardPersonasCard`
 * heeft de laadvlag van `useFirestoreLoaded` nodig, en `PersonasPresenceWidget`
 * vraagt alleen de actieve deelnemers op. Zouden die de verrijking overslaan,
 * dan filteren ze op het oude veld terwijl de rest op het rooster kijkt — en
 * dat is precies de scheefstand die dit oplost.
 *
 * @param {Array<object>|*} personas - Wat je hook teruggaf
 * @returns {Array<object>|*} Dezelfde waarde, met `LocationIDs` per deelnemer
 */
const useWithPersonaLocations = (personas) => {
    const scheduleTasks = useSharedCollectionWhere('Tasks', 'Type', 'personaSchedule')

    const index = useMemo(
        () => buildPersonaLocationIndex(scheduleTasks),
        [scheduleTasks],
    )

    return useMemo(
        // Geen array doorlaten zoals hij is: afnemers gebruiken `undefined` om
        // 'nog aan het laden' van 'geen deelnemers' te onderscheiden, en een
        // lege lijst zou dat verschil wegpoetsen.
        // AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
        () => (Array.isArray(personas) ? withPersonaLocations(personas, index) : personas),
        [personas, index],
    )
}

export { useWithPersonaLocations }
export default usePersonaLocations
